import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Paper, Breadcrumbs, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useNotification } from '../../../components/Notification';
import { Add as AddIcon, Delete as DeleteIcon, Category as CategoryIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import MultiSelect from '../../../components/MultiSelect';
import AddIdentityTypeModal from './AddIdentityTypeModal';
import { hasPermission } from '../../../utils/permissions';
import {
    fetchIdentityTypes,
    fetchIdentityTypeById,
    addIdentityType,
    updateIdentityType,
    selectIdentityTypes,
    selectIdentityTypesLoading,
    selectIdentityTypesError,
    selectIdentityTypesSuccessMessage,
    selectIdentityTypesTotal,
    selectIdentityTypesPage,
    selectIdentityTypesPageSize,
    selectIdentityTypesSortColumn,
    selectIdentityTypesSortDirection,
    selectIdentityTypesFilterName,
    selectIdentityTypesFilterIsActive,
    selectSelectedIdentityType,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    setFilterIsActive,
    deleteIdentityType,
    bulkDeleteIdentityTypes,
    clearError,
    clearSuccessMessage,
} from './slice';
import './IdentityTypes.css';

function IdentityTypes() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const identityTypes = useSelector(selectIdentityTypes);
    const loading = useSelector(selectIdentityTypesLoading);
    const error = useSelector(selectIdentityTypesError);
    const successMessage = useSelector(selectIdentityTypesSuccessMessage);
    const total = useSelector(selectIdentityTypesTotal);
    const page = useSelector(selectIdentityTypesPage);
    const pageSize = useSelector(selectIdentityTypesPageSize);
    const sortColumn = useSelector(selectIdentityTypesSortColumn);
    const sortDirection = useSelector(selectIdentityTypesSortDirection);
    const filterName = useSelector(selectIdentityTypesFilterName);
    const filterIsActive = useSelector(selectIdentityTypesFilterIsActive);
    const selectedIdentityType = useSelector(selectSelectedIdentityType);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [identityTypeToDelete, setIdentityTypeToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // Check permissions
    const canAddIdentityType = hasPermission('identitytype', 'addIdentityType');
    const canEditIdentityType = hasPermission('identitytype', 'editIdentityType');
    const canDeleteIdentityType = hasPermission('identitytype', 'deleteIdentityType');

    useEffect(() => {
        dispatch(fetchIdentityTypes({ page, pageSize, sortColumn, sortDirection, filterName, filterIsActive }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, filterIsActive]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch identity types to update the list
            dispatch(fetchIdentityTypes({ page, pageSize, sortColumn, sortDirection, filterName, filterIsActive }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName, filterIsActive]);

    // Helper function to escape HTML and prevent XSS
    const escapeHtml = (text) => {
        if (!text) return '-';
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    // Define table columns
    const columns = [
        {
            key: 'name',
            label: t('identityTypes.table.name'),
        },
        {
            key: 'description',
            label: t('identityTypes.table.description'),
            render: (value) => escapeHtml(value),
        },
        {
            key: 'isActive',
            label: t('identityTypes.table.status'),
            render: (value) => {
                const isActive = value === true || value === 1 || value === '1';
                const hasStatus = value !== undefined && value !== null;

                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: hasStatus ? (isActive ? '#d4edda' : '#f8d7da') : '#e2e8f0',
                            color: hasStatus ? (isActive ? '#155724' : '#721c24') : '#64748b',
                        }}
                    >
                        {hasStatus ? (isActive ? t('identityTypes.status.active') : t('identityTypes.status.inactive')) : '-'}
                    </Box>
                );
            },
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch identity type details by ID
        dispatch(fetchIdentityTypeById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setIdentityTypeToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (identityTypeToDelete?.id) {
            dispatch(deleteIdentityType(identityTypeToDelete.id));
        }
        setShowDeleteDialog(false);
        setIdentityTypeToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setIdentityTypeToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteIdentityTypes(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle filter name change
    const handleFilterNameChange = (event) => {
        dispatch(setFilterName(event.target.value));
    };

    // Handle filter status change (pass raw value like Vehicles page)
    const handleFilterStatusChange = (event) => {
        dispatch(setFilterIsActive(event.target.value));
    };

    // Handle add identity type - show dialog
    const handleAddIdentityType = () => {
        setShowAddDialog(true);
    };

    // Handle close add identity type dialog
    const handleCloseAddIdentityType = () => {
        setShowAddDialog(false);
    };

    // Confirm add identity type
    const handleConfirmAddIdentityType = (identityTypeData) => {
        dispatch(addIdentityType(identityTypeData));
        setShowAddDialog(false);
    };

    // Handle close edit identity type dialog
    const handleCloseEditIdentityType = () => {
        setShowEditDialog(false);
    };

    // Confirm edit identity type
    const handleConfirmEditIdentityType = (identityTypeData) => {
        dispatch(updateIdentityType(identityTypeData));
        setShowEditDialog(false);
    };

    // Status filter options
    const statusOptions = [
        { id: '', name: t('identityTypes.filter.allStatus') },
        { id: 'true', name: t('identityTypes.status.active') },
        { id: 'false', name: t('identityTypes.status.inactive') },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CategoryIcon fontSize="small" />
                        <Typography variant="body2">{t('identityTypes.breadcrumb.catalog')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('identityTypes.breadcrumb.identityTypes')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('identityTypes.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddIdentityType && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddIdentityType}
                                id="addIdentityType"
                                name="addIdentityType"
                                data-testid="identityTypes-button-addIdentityType"
                            >
                                {t('identityTypes.addIdentityType')}
                            </Button>
                        )}
                        {canDeleteIdentityType && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0}
                                id="deleteSelected"
                                name="deleteSelected"
                                data-testid="identityTypes-button-deleteSelected"
                            >
                                {t('identityTypes.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Input
                            label={t('identityTypes.filterByName')}
                            value={filterName}
                            onChange={handleFilterNameChange}
                            placeholder={t('identityTypes.filterByNamePlaceholder')}
                            id="filterName"
                            name="filterName"
                            data-testid="identityTypes-input-filterName"
                            sx={{ width: 320 }}
                        />
                        <MultiSelect
                            label={t('identityTypes.filterByStatus')}
                            value={filterIsActive}
                            onChange={handleFilterStatusChange}
                            options={statusOptions}
                            id="filterStatus"
                            name="filterStatus"
                            data-testid="identityTypes-select-filterStatus"
                            sx={{ width: 320 }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="identityTypes-table"
                        columns={columns}
                        data={identityTypes}
                        selectable={true}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        onEdit={canEditIdentityType ? handleEdit : null}
                        onDelete={canDeleteIdentityType ? handleDelete : null}
                        loading={loading}
                        emptyMessage={t('identityTypes.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={['name', 'isActive']}
                    />
                </Box>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('identityTypes.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('identityTypes.deleteDialog.message', {
                            name: identityTypeToDelete?.name || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="identityTypes-button-cancelDelete"
                    >
                        {t('identityTypes.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="identityTypes-button-confirmDelete"
                    >
                        {t('identityTypes.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('identityTypes.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('identityTypes.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="identityTypes-button-cancelBulkDelete"
                    >
                        {t('identityTypes.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="identityTypes-button-confirmBulkDelete"
                    >
                        {t('identityTypes.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Identity Type Modal */}
            <AddIdentityTypeModal open={showAddDialog} onClose={handleCloseAddIdentityType} onConfirm={handleConfirmAddIdentityType} />

            {/* Edit Identity Type Modal */}
            <AddIdentityTypeModal
                open={showEditDialog}
                onClose={handleCloseEditIdentityType}
                onConfirm={handleConfirmEditIdentityType}
                identityTypeData={selectedIdentityType}
                editMode={true}
            />
        </Box>
    );
}

export default IdentityTypes;
